import { inject, injectable } from 'tsyringe';
import { RecommendationDatastoreService } from '../../datastore-service/recommendation-datastore.service';
import { PersistentStorageService } from '../../persistent-storage-service/persistent-storage.service';

import { ICondition, MatchResult } from './interfaces/condition.interface';
import { LoggerService } from '../../../../shared/services/logger.service';
import {
  conditionType,
  ConditionType,
} from './decrators/condition-type.decorator';
import {
  IPromotionWindowManagerService,
  IPromotionWindowManagerServiceDelegate,
  kNameOfPromotionWindowManagerService,
} from '../../../interfaces/promotion-window-manager-service.interface';
import { NoRecommendationReasons } from '../no-recommendations-reasons.enums';
import { ConditionContext } from './interfaces/condition-context.interface';
import { PromotionType } from '../../datastore-service/models/raw-promotion.model';
import { Promotion } from '../../../models/promotion.model';
import { PromotionDisplayDetails } from '../../../../shared/models/promotion-display-details.model';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.Promotion)
export class ConditionCooldownFromLastPromotion
  implements ICondition, IPromotionWindowManagerServiceDelegate
{
  private readonly logger = LoggerService.getCategory(
    'ConditionCooldownFromLastPromotion',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly persistentStorageService: PersistentStorageService,
    @inject(kNameOfPromotionWindowManagerService)
    private readonly notifWindowManager: IPromotionWindowManagerService,
  ) {
    this.notifWindowManager.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    const { promotion } = context;

    const configs = await this.datastoreService.getGlobalConfigurations();
    if (!configs) {
      this.logger.warn('Failed to get server configurations - returning false');
      return [false, NoRecommendationReasons.FailedToGetServiceConfig];
    }

    const cooldownHrs =
      promotion?.type === PromotionType.Offer
        ? configs.cooldownFromPreviousPromoOfferInHours
        : configs.cooldownFromPreviousPromoInHours;

    const lastPromotionTimestamp = this.getLastPromotionTimestamp(promotion);

    const previousPromotionCooldownExpired: boolean =
      this.isCooldownHrsFromPreviousPromotionExpired(
        lastPromotionTimestamp,
        cooldownHrs,
      );

    if (!previousPromotionCooldownExpired) {
      return [false, NoRecommendationReasons.CooldownDisplay];
    }

    return [true, undefined];
  }

  // ---------------------------------------------------------------------------
  private getLastPromotionTimestamp(promotion: Promotion): number {
    if (promotion?.type === PromotionType.Offer) {
      return this.persistentStorageService.getLastDisplayedOfferTimestamp();
    }

    return this.persistentStorageService.getLastDisplayedPromotionTimestamp();
  }

  // ---------------------------------------------------------------------------
  private isCooldownHrsFromPreviousPromotionExpired(
    lastPromotionTimestamp: number,
    cooldownHrs: number,
  ): boolean {
    if (!lastPromotionTimestamp) {
      return true;
    }

    const timePassedInMs = Date.now() - lastPromotionTimestamp;
    const timePassedHrs = timePassedInMs / (1000 * 60 * 60);
    const matches = timePassedHrs >= cooldownHrs;

    const msg = `Previous promotion displayed: ${Math.floor(
      timePassedHrs,
    )} hrs ago, config:${cooldownHrs} hrs, ret:${matches}`;

    this.logger.info(msg);
    return matches;
  }

  // ---------------------------------------------------------------------------
  // IPromotionWindowManagerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onPromotionWindowClosed(
    info: PromotionDisplayDetails,
    _visibleTimeInMs: number,
  ) {
    this.logger.info(
      'Updating lastDisplayedPromotionTimestamp with current ' +
        `timestamp: ${Date.now()}`,
    );

    if (info?.promotionType === PromotionType.Offer) {
      this.persistentStorageService.setLastDisplayedOfferTimestamp(Date.now());
    } else {
      this.persistentStorageService.setLastDisplayedPromotionTimestamp(
        Date.now(),
      );
    }
  }
}
